<!--
	Why is this subscription in this state? Everything the store holds about it, from every log that
	has a piece of the answer: the stored row, where it came from if it was imported, the audit and
	provider-event logs merged into one timeline, what the provider says right now, and what each
	entitlement change it caused told the consuming applications — with their answers.

	Its own component and its own load, because it is the heaviest read on the page and a failure in
	it must not cost support the rest of the page. Every section degrades to saying what it could not
	show rather than disappearing: a missing section reads as "nothing happened".
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import { Badge, Button, Table, TableBody, TableBodyCell, TableBodyRow, TableHead, TableHeadCell } from 'flowbite-svelte';
	import { EmptyState, Panel, RoutedLink } from '../../../components';
	import {
		getAdminSubscriptionDiagnostics,
		getAdminSubscriptionProviderState,
		type SubscriptionDiagnostics,
		type SubscriptionProviderState
	} from '../../../AppNetwork';

	export let uuid: string;

	let diagnostics: SubscriptionDiagnostics | null = null;
	let loadError: string = '';
	let providerState: SubscriptionProviderState | null = null;
	let providerBusy: boolean = false;
	let providerError: string = '';

	async function load(): Promise<void> {
		loadError = '';
		try {
			diagnostics = await getAdminSubscriptionDiagnostics(uuid);
		} catch (error) {
			loadError = (error as Error).message;
		}
	}

	/** Ask the provider now. A button, because it is a live call against the merchant account. */
	async function askProvider(): Promise<void> {
		providerBusy = true;
		providerError = '';
		try {
			providerState = await getAdminSubscriptionProviderState(uuid);
		} catch (error) {
			providerError = (error as Error).message;
		} finally {
			providerBusy = false;
		}
	}

	onMount(load);

	/** A date and time, in the reader's zone, to the second — the order of events is the point. */
	function when(value: unknown): string {
		if (value === null || value === undefined || value === '') return '—';
		const date = new Date(String(value));
		return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'medium' });
	}

	/** Any stored value as text a person can read, without losing what kind of value it was. */
	function show(value: unknown): string {
		if (value === null || value === undefined) return '—';
		if (typeof value === 'object') return JSON.stringify(value);
		return String(value);
	}

	const json = (value: unknown): string => JSON.stringify(value, null, 2);

	const SOURCE_LABEL: Record<string, string> = { audit: 'Audit', provider_event: 'Provider event', change: 'Plan change' };
	const SOURCE_COLOR: Record<string, 'blue' | 'purple' | 'yellow'> = { audit: 'blue', provider_event: 'purple', change: 'yellow' };

	/** Colour for a status word that is bad news on its face, so the eye finds it in a long list. */
	function statusColor(status: string | null): 'red' | 'green' | 'yellow' | 'dark' {
		if (status === null) return 'dark';
		if (/fail|dead|error|canceled|cancelled|revoked|expired|refused/i.test(status)) return 'red';
		if (/deliver|processed|created|active|succeeded|applied|complete/i.test(status)) return 'green';
		if (/pending|retry|skipped|ignored|past_due|grace/i.test(status)) return 'yellow';
		return 'dark';
	}

	$: rowEntries = diagnostics === null ? [] : Object.entries(diagnostics.row).sort(([left], [right]) => left.localeCompare(right));
</script>

<Panel title="Diagnostics" faIcon="stethoscope">
	{#if loadError !== ''}
		<EmptyState variant="error" title="The diagnostics could not be loaded" description={loadError} />
	{:else if diagnostics === null}
		<EmptyState variant="loading" title="Reading every log about this subscription…" />
	{:else}
		<div class="space-y-6 min-w-0">
			<!-- The provider's word beside ours: the first thing to rule in or out. -->
			<section>
				<h3 class="card-label">Payment provider</h3>
				<p class="text-sm mt-1">
					{#if diagnostics.provider === null}
						The provider row is gone.
					{:else}
						{diagnostics.provider.name} · {diagnostics.provider.type} · {diagnostics.provider.mode}
					{/if}
					· <span class="font-mono text-xs">{show(diagnostics.row.providerSubscriptionID)}</span>
				</p>
				<Button size="xs" color="alternative" class="mt-2" disabled={providerBusy} on:click={askProvider}>
					{providerBusy ? 'Asking the provider…' : 'Compare with the provider now'}
				</Button>
				{#if providerError !== ''}
					<p class="text-red-500 text-sm mt-2">{providerError}</p>
				{/if}
				{#if providerState !== null}
					<div class="mt-2 text-sm">
						{#if providerState.error !== null}
							<p class="text-red-500">The provider could not be read: {providerState.error}</p>
						{:else if providerState.differences.length === 0}
							<p>The provider agrees with this store on every compared field ({when(providerState.fetchedAt)}).</p>
						{:else}
							<p class="text-red-500">
								{providerState.differences.length} field(s) disagree ({when(providerState.fetchedAt)}):
							</p>
							<div class="min-w-0 overflow-x-auto mt-1">
								<Table class="dataTable">
									<TableHead>
										<TableHeadCell>Field</TableHeadCell>
										<TableHeadCell>This store</TableHeadCell>
										<TableHeadCell>Provider</TableHeadCell>
									</TableHead>
									<TableBody>
										{#each providerState.differences as difference (difference.field)}
											<TableBodyRow>
												<TableBodyCell>{difference.field}</TableBodyCell>
												<TableBodyCell>{show(difference.local)}</TableBodyCell>
												<TableBodyCell>{show(difference.provider)}</TableBodyCell>
											</TableBodyRow>
										{/each}
									</TableBody>
								</Table>
							</div>
						{/if}
						{#if providerState.remote !== null}
							<details class="mt-2">
								<summary class="cursor-pointer text-default text-xs">The provider's answer, as mapped</summary>
								<pre class="text-xs overflow-x-auto mt-1">{json(providerState.remote)}</pre>
							</details>
						{/if}
					</div>
				{/if}
			</section>

			<!-- Where it came from. Only for an imported subscription; the rest were sold here. -->
			{#if diagnostics.legacy !== null}
				<section>
					<h3 class="card-label">Imported from the legacy store</h3>
					<dl class="grid gap-x-4 gap-y-1 text-sm mt-1 sm:grid-cols-[max-content_1fr]">
						<dt class="text-default">Legacy id</dt>
						<dd class="font-mono text-xs break-all">{diagnostics.legacy.sourceID}</dd>
						<dt class="text-default">Batch</dt>
						<dd>
							{#if diagnostics.legacy.batch === null}
								No staged record is left for it.
							{:else}
								<span class="font-mono text-xs">{diagnostics.legacy.batch.uuid}</span>
								· {diagnostics.legacy.batch.status} · applied {when(diagnostics.legacy.batch.appliedAt)}
							{/if}
						</dd>
						<dt class="text-default">Import</dt>
						<dd>
							{#if diagnostics.legacy.outcome === null}
								No outcome recorded.
							{:else}
								<Badge color={statusColor(diagnostics.legacy.outcome.outcome)}>{diagnostics.legacy.outcome.outcome}</Badge>
								{when(diagnostics.legacy.outcome.at)}
								{#if diagnostics.legacy.outcome.detail !== null}
									<span class="block font-mono text-xs break-all">{show(diagnostics.legacy.outcome.detail)}</span>
								{/if}
							{/if}
						</dd>
						{#if diagnostics.legacy.record !== null}
							<dt class="text-default">Legacy status</dt>
							<dd>
								{show(diagnostics.legacy.record.status)} · local {show(diagnostics.legacy.record.localSubscriptionStatus)}
								· terminated {show(diagnostics.legacy.record.isTerminated)}
								{#if diagnostics.legacy.record.pastDueReason}
									· {show(diagnostics.legacy.record.pastDueReason)}
								{/if}
							</dd>
							<dt class="text-default">Legacy period</dt>
							<dd>
								{show(diagnostics.legacy.record.billingPeriodStartDate)} – {show(diagnostics.legacy.record.billingPeriodEndDate)}
								· next bill {show(diagnostics.legacy.record.nextBillingDate)}
							</dd>
						{/if}
					</dl>
					{#if diagnostics.legacy.record !== null}
						<details class="mt-2">
							<summary class="cursor-pointer text-default text-xs">The exported legacy record, verbatim</summary>
							<pre class="text-xs overflow-x-auto mt-1">{json(diagnostics.legacy.record)}</pre>
						</details>
					{/if}
					{#if diagnostics.legacy.charges.length > 0}
						<div class="min-w-0 overflow-x-auto mt-2">
							<Table class="dataTable">
								<TableHead>
									<TableHeadCell>Legacy charge</TableHeadCell>
									<TableHeadCell>Legacy</TableHeadCell>
									<TableHeadCell>Amount</TableHeadCell>
									<TableHeadCell>Import</TableHeadCell>
								</TableHead>
								<TableBody>
									{#each diagnostics.legacy.charges as charge (charge.sourceID)}
										<TableBodyRow>
											<TableBodyCell>
												<span class="font-mono text-xs">{charge.sourceID}</span>
												{#if charge.braintreeTransactionID !== null}
													<span class="block font-mono text-xs text-default">{charge.braintreeTransactionID}</span>
												{/if}
											</TableBodyCell>
											<TableBodyCell>{[charge.kind, charge.status, charge.date].filter(Boolean).join(' · ')}</TableBodyCell>
											<TableBodyCell class="tabular-nums">{show(charge.amount)} {charge.currency?.toUpperCase() ?? ''}</TableBodyCell>
											<TableBodyCell>
												{#if charge.outcome === null}
													<span class="text-default text-xs">not recorded</span>
												{:else}
													<Badge color={statusColor(charge.outcome)}>{charge.outcome}</Badge>
												{/if}
												{#if charge.reason !== null}
													<span class="block text-xs text-default">{charge.reason}</span>
												{/if}
											</TableBodyCell>
										</TableBodyRow>
									{/each}
								</TableBody>
							</Table>
						</div>
					{/if}
				</section>
			{/if}

			<!-- One timeline: who moved it and why, what the provider announced, and every plan change. -->
			<section>
				<h3 class="card-label">Timeline</h3>
				{#if diagnostics.timeline.length === 0}
					<p class="text-default text-sm mt-1">Nothing is recorded about this subscription in any log.</p>
				{:else}
					<div class="min-w-0 overflow-x-auto mt-1">
						<Table class="dataTable">
							<TableHead>
								<TableHeadCell>When</TableHeadCell>
								<TableHeadCell>From</TableHeadCell>
								<TableHeadCell>What</TableHeadCell>
								<TableHeadCell>Detail</TableHeadCell>
							</TableHead>
							<TableBody>
								{#each diagnostics.timeline as entry, index (index)}
									<TableBodyRow>
										<TableBodyCell class="tabular-nums">{when(entry.at)}</TableBodyCell>
										<TableBodyCell><Badge color={SOURCE_COLOR[entry.source]}>{SOURCE_LABEL[entry.source]}</Badge></TableBodyCell>
										<TableBodyCell>
											<span class="font-mono text-xs">{entry.what}</span>
											{#if entry.summary !== null}
												<span class="block text-xs" class:text-red-500={statusColor(entry.summary) === 'red'}>{entry.summary}</span>
											{/if}
										</TableBodyCell>
										<TableBodyCell>
											{#if entry.detail !== null}
												<details>
													<summary class="cursor-pointer text-default text-xs">Everything recorded</summary>
													<pre class="text-xs overflow-x-auto mt-1 max-w-[40rem]">{json(entry.detail)}</pre>
												</details>
											{/if}
										</TableBodyCell>
									</TableBodyRow>
								{/each}
							</TableBody>
						</Table>
					</div>
				{/if}
			</section>

			<!-- The chain down to the applications: what they were told, and what they said back. -->
			<section>
				<h3 class="card-label">What the applications were told</h3>
				{#if diagnostics.entitlements.length === 0}
					<p class="text-default text-sm mt-1">
						No entitlement exists for this subscription or its product's key — so no application has
						been told anything about it.
					</p>
				{/if}
				{#each diagnostics.entitlements as entitlement (entitlement.entitlementKey)}
					<div class="widget bg-depth mt-2 text-sm">
						<p>
							<span class="font-mono text-xs">{entitlement.entitlementKey}</span>
							<Badge color={statusColor(entitlement.status)}>{entitlement.status}</Badge>
							· {entitlement.source} · version {entitlement.version} · {entitlement.seats} seat(s)
							{#if !entitlement.grantedByThisSubscription}
								· <span class="text-amber-600 dark:text-amber-400">granted by another purchase, not this subscription</span>
							{/if}
						</p>
						<p class="text-default text-xs mt-1">
							Expires {when(entitlement.expiresAt)} · grace until {when(entitlement.graceUntil)}
							{#if entitlement.revokedAt !== null}
								· revoked {when(entitlement.revokedAt)} ({show(entitlement.revokedReason)})
							{/if}
						</p>
						{#each entitlement.changes as change (change.id)}
							<div class="mt-2 border-t border-widget-color pt-2">
								<p class="text-xs">
									<span class="font-semibold">v{change.version} · {change.changeType}</span> · {when(change.occurredAt)}
								</p>
								{#if change.deliveries.length === 0}
									<p class="text-default text-xs">Delivered to no application.</p>
								{/if}
								{#each change.deliveries as delivery}
									<div class="text-xs mt-1">
										{delivery.application}
										<Badge color={statusColor(delivery.status)}>{delivery.status}</Badge>
										{delivery.isResync ? '(resync)' : ''}
										· {delivery.attemptCount} attempt(s)
										· last {when(delivery.lastAttemptAt)}
										{#if delivery.lastResponseStatus !== null}· HTTP {delivery.lastResponseStatus}{/if}
										{#if delivery.lastError !== null}<span class="block text-red-500">{delivery.lastError}</span>{/if}
										{#if delivery.lastResponseBody !== null}
											<details>
												<summary class="cursor-pointer text-default">What the application answered</summary>
												<pre class="overflow-x-auto mt-1">{delivery.lastResponseBody}</pre>
											</details>
										{/if}
									</div>
								{/each}
								<details class="text-xs mt-1">
									<summary class="cursor-pointer text-default">What was sent (the change snapshot)</summary>
									<pre class="overflow-x-auto mt-1">{json(change.snapshot)}</pre>
								</details>
							</div>
						{/each}
					</div>
				{/each}

				<!-- An application that is not scoped has no delivery rows at all, so the list above
				     cannot show it — this is where "why did nothing reach it" is answered. -->
				<div class="min-w-0 overflow-x-auto mt-3">
					<Table class="dataTable">
						<TableHead>
							<TableHeadCell>Application</TableHeadCell>
							<TableHeadCell>Hears about these keys</TableHeadCell>
							<TableHeadCell>Active</TableHeadCell>
							<TableHeadCell>Webhook</TableHeadCell>
						</TableHead>
						<TableBody>
							{#each diagnostics.applications as application (application.name)}
								<TableBodyRow>
									<TableBodyCell>
										<RoutedLink class="text-primary-600 dark:text-primary-400 hover:underline" to="/Settings/Admin/Applications">{application.name}</RoutedLink>
									</TableBodyCell>
									<TableBodyCell>{application.isScoped ? 'Yes' : 'No — not scoped to them'}</TableBodyCell>
									<TableBodyCell>{application.isActive ? 'Yes' : 'No'}</TableBodyCell>
									<TableBodyCell>{application.hasWebhookURL ? 'Pushed' : 'Pull only'}</TableBodyCell>
								</TableBodyRow>
							{/each}
						</TableBody>
					</Table>
				</div>
			</section>

			<!-- The row as stored, every column, including the ones the page above does not show. -->
			<section>
				<h3 class="card-label">The stored row</h3>
				<dl class="grid gap-x-4 gap-y-1 text-xs mt-1 sm:grid-cols-[max-content_1fr]">
					{#each rowEntries as [column, value] (column)}
						<dt class="text-default font-mono">{column}</dt>
						<dd class="font-mono break-all">{show(value)}</dd>
					{/each}
				</dl>
			</section>
		</div>
	{/if}
</Panel>
